import type { ComponentProps, ReactNode } from 'react'
import { useId } from 'react'

import { cn } from '@/lib/cn'

export const controlClass =
  'min-h-12 w-full rounded-control border border-line-strong bg-surface px-4 text-base text-ink transition placeholder:text-ink-faint focus:border-brand focus:ring-4 focus:ring-brand/12 focus-visible:outline-none disabled:opacity-50'

interface FieldProps {
  label: ReactNode
  hint?: ReactNode
  error?: ReactNode
  className?: string
  children: (control: {
    id: string
    'aria-describedby': string | undefined
    'aria-invalid': true | undefined
  }) => ReactNode
}

/* Accessible field scaffold: explicit label association plus described-by
   wiring for hint and error copy. */
export function Field({ label, hint, error, className, children }: FieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  return (
    <div className={cn('grid gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-semibold text-ink">
        {label}
      </label>
      {children({
        id,
        'aria-describedby':
          [errorId, hintId].filter(Boolean).join(' ') || undefined,
        'aria-invalid': error ? true : undefined,
      })}
      {hint && (
        <p id={hintId} className="text-sm text-ink-soft">
          {hint}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          role="alert"
          className="text-sm font-semibold text-danger"
        >
          {error}
        </p>
      )}
    </div>
  )
}

export function Input({ className, ...rest }: ComponentProps<'input'>) {
  return <input className={cn(controlClass, className)} {...rest} />
}

export function Textarea({ className, ...rest }: ComponentProps<'textarea'>) {
  return (
    <textarea
      className={cn(controlClass, 'min-h-24 py-3', className)}
      {...rest}
    />
  )
}

export function Select({
  className,
  children,
  ...rest
}: ComponentProps<'select'>) {
  return (
    <select
      className={cn(controlClass, 'appearance-none', className)}
      {...rest}
    >
      {children}
    </select>
  )
}
